import { ArrowDownRight, Sparkles } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

export default function Hero() {
  const { t } = useT();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100vh] overflow-hidden pt-32 pb-24"
    >
      <div className="absolute inset-0 ambient-grid opacity-60 pointer-events-none" />
      <div className="absolute inset-0 grain pointer-events-none" />

      <div className="blob blob-3 float-slow" style={{ width: 520, height: 520, top: "-120px", left: "-160px" }} data-parallax="0.15" />
      <div className="blob blob-1 float-reverse" style={{ width: 420, height: 420, top: "10%", right: "-120px" }} data-parallax="0.25" />
      <div className="blob blob-2" style={{ width: 300, height: 300, bottom: "-80px", left: "30%", opacity: 0.35 }} data-parallax="-0.1" />

      <div className="absolute top-[22%] right-[10%] hidden md:block" data-parallax="-0.2" data-rot="0.02">
        <div className="h-28 w-28 rounded-3xl iridescent shadow-2xl opacity-80 spin-slow" />
      </div>
      <div className="absolute top-[60%] left-[8%] hidden md:block" data-parallax="0.18">
        <div className="h-20 w-20 rounded-full border-2 border-[#A49BFF] float-slow" />
      </div>
      <div className="absolute top-[12%] left-[38%] hidden lg:block" data-parallax="-0.1">
        <div className="h-16 w-16 rotate-45 float-reverse" style={{ background: "linear-gradient(135deg, #00F0FF, #FF00E5)", borderRadius: 6 }} />
      </div>

      <div className="container mx-auto px-6 md:px-12 max-w-7xl relative">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-9">
            <div className="reveal flex items-center gap-3 mb-10">
              <span className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5C527A]">
                <Sparkles className="h-3.5 w-3.5 text-[#A49BFF]" />
                {t("hero.badge")}
              </span>
            </div>

            <h1 data-testid="hero-title" className="font-display font-extralight text-[14vw] md:text-[10vw] lg:text-[9rem] xl:text-[10.5rem] leading-[0.88] tracking-[-0.055em] text-[#0F0524]">
              <span className="block reveal">MARCHUK</span>
              <span className="block reveal" style={{ transitionDelay: "120ms" }}>
                <span className="italic font-light grad-text">LLC</span>
              </span>
            </h1>

            <div className="mt-10 grid grid-cols-12 gap-6">
              <div className="col-span-12 md:col-span-7 reveal" style={{ transitionDelay: "220ms" }}>
                <p className="font-display text-xl md:text-2xl lg:text-3xl font-light leading-[1.25] text-[#0F0524] max-w-2xl">
                  <span className="grad-text font-medium">{t("hero.taglineA")}</span>{" "}
                  <span>{t("hero.taglineB")}</span>
                </p>
                <p className="mt-6 text-[15px] md:text-base text-[#5C527A] max-w-xl leading-relaxed">
                  {t("hero.desc")}
                </p>
              </div>

              <div className="col-span-12 md:col-span-5 md:flex md:justify-end reveal" style={{ transitionDelay: "320ms" }}>
                <div className="flex flex-col gap-3 w-full md:max-w-xs">
                  <button
                    data-testid="hero-cta-primary"
                    onClick={() => scrollTo("services")}
                    className="btn-sheen group inline-flex items-center justify-between gap-3 rounded-full px-6 py-4 text-[14px] font-semibold text-white transition-all hover:shadow-[0_10px_40px_-10px_rgba(255,0,229,0.45)]"
                    style={{ background: "linear-gradient(135deg, #0F0524 0%, #2A1458 100%)" }}
                  >
                    {t("hero.ctaServices")}
                    <span className="h-8 w-8 grid place-items-center rounded-full iridescent">
                      <ArrowDownRight className="h-4 w-4 text-[#0F0524]" />
                    </span>
                  </button>
                  <button
                    data-testid="hero-cta-secondary"
                    onClick={() => scrollTo("contact")}
                    className="glow-border glass inline-flex items-center justify-between gap-3 rounded-full px-6 py-4 text-[14px] font-semibold text-[#0F0524]"
                  >
                    {t("hero.ctaContact")}
                    <span className="text-[#A49BFF]">→</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="hidden lg:block col-span-3">
            <div className="reveal-clip relative aspect-[3/4] rounded-3xl overflow-hidden glass" data-parallax="-0.05">
              <div className="absolute inset-0 iridescent opacity-70" />
              <div className="absolute inset-0 grain" />
              <div className="absolute bottom-5 left-5 right-5 text-[#0F0524]">
                <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">{t("hero.sideLabel")}</div>
                <div className="font-display text-2xl font-light mt-2 leading-tight">
                  {t("hero.sideHeading")}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-24 flex items-center gap-4 reveal" style={{ transitionDelay: "420ms" }}>
          <span className="h-[1px] w-16 bg-[#0F0524]/30" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#5C527A]">
            {t("hero.scrollHint")}
          </span>
          <span className="inline-block h-4 w-4 rounded-full iridescent float-slow" />
        </div>
      </div>
    </section>
  );
}
